import axios, { isAxiosError } from 'axios'
import { readApiProblem } from './problem'

/** The default TanStack retry budget for a query that failed for a genuinely transient reason. */
export const QUERY_RETRY_LIMIT = 3

/**
 * BDI-5010: a ClickHouse workload (fleet analytics, or device reads) was at capacity and the
 * backend shed the request immediately instead of queueing it. Transient, so it is worth a
 * retry, but only a couple and only after the workload has had a few seconds to drain: an
 * immediate 1 s retry from every panel on a dashboard is the burst that filled it.
 */
export const ANALYTICS_BUSY = 'BDI-5010'
/**
 * BDI-5011: the read ran past its server-side budget (25 s analytics, 15 s device reads) and was
 * cancelled. The same request will most likely time out again; only a narrower request helps.
 */
export const QUERY_TIMEOUT = 'BDI-5011'

/** Retries after the first BDI-5010 failure, so at most three attempts in all. */
export const ANALYTICS_BUSY_RETRY_LIMIT = 2

const NON_RETRYABLE_HTTP_STATUSES = new Set([400, 401, 403, 404, 422])

/**
 * Default `retry` predicate for the QueryClient.
 *
 * A client-side timeout (axios `ECONNABORTED`/`ETIMEDOUT`) or a cancelled request is not a
 * transient failure: the backend does not cancel the work when the browser gives up, so every
 * retry starts one more copy of a request that is still executing server-side. Dev
 * 2026-09-21: one dashboard visit re-issued a slow ClickHouse read 4–5× (30 s timeout + 1/2/4 s
 * backoff) and pinned the shared pool for minutes. Polling hooks re-fetch on their own cadence.
 * Deterministic input, access and not-found responses also settle without a retry.
 * Everything else keeps the limited retry budget.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (axios.isCancel(error)) return false
  if (isAxiosError(error) && (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT')) return false
  // Axios has already completed any permitted DPoP nonce/refresh recovery. An outer
  // retry must not restart it. Trust the HTTP status even without a Problem Details body.
  if (isAxiosError(error) && error.response && NON_RETRYABLE_HTTP_STATUSES.has(error.response.status)) return false
  const code = readApiProblem(error)?.code ?? ''
  // BDI-5009: BDI's declared "State service unavailable — retry later". Retrying it at
  // 1/2/4 s from every open device panel turns one State outage into a reconnect burst
  // against a recovering service; the polling cadence retries on its own.
  if (NON_RETRYABLE_PROBLEMS.has(code)) return false
  if (code === ANALYTICS_BUSY) return failureCount < ANALYTICS_BUSY_RETRY_LIMIT
  return failureCount < QUERY_RETRY_LIMIT
}

// BDI-6005: more than 100 active OTA releases in the attention scope (backend 4933a6a). The
// same request can only fail again; the operator has to narrow the project filter.
// BDI-5011: a server-side query timeout, treated like the client-side timeout above.
const NON_RETRYABLE_PROBLEMS = new Set(['BDI-5009', 'BDI-6005', QUERY_TIMEOUT])

/**
 * Jittered windows for the BDI-5010 retries, indexed by the failure count TanStack passes
 * (0 before the first retry). Jitter spreads the panels of one page apart, so a page with
 * eight busy panels does not come back as one synchronized burst.
 */
const BUSY_RETRY_WINDOWS_MS: ReadonlyArray<readonly [number, number]> = [
  [2_000, 4_000],
  [5_000, 8_000],
]

/** TanStack's own default: 1 s, 2 s, 4 s … capped at 30 s. */
const defaultRetryDelay = (failureCount: number): number => Math.min(1_000 * 2 ** failureCount, 30_000)

/**
 * Default `retryDelay` for the QueryClient. Only BDI-5010 gets the longer jittered backoff;
 * every other retried error keeps TanStack's default exponential delay.
 *
 * @param random injected by tests; defaults to `Math.random`
 */
export function queryRetryDelay(failureCount: number, error: unknown, random: () => number = Math.random): number {
  if (readApiProblem(error)?.code !== ANALYTICS_BUSY) return defaultRetryDelay(failureCount)
  const [min, max] = BUSY_RETRY_WINDOWS_MS[Math.min(failureCount, BUSY_RETRY_WINDOWS_MS.length - 1)]
  return Math.round(min + random() * (max - min))
}
